/**
 * Czas Pierwszej reakcji na zgłoszenia wsparcia (migracja 858, dokument 21 §2).
 *
 * Stan reakcji NIE jest liczony tutaj: czyta się go z widoku
 * `wsparcie_reakcje` (security_invoker — RLS zgłoszeń decyduje, co widać).
 * Termin z Pakietu wsparcia, migawka pakietu i chwila wpływu żyją w bazie;
 * druga implementacja kalendarza w przeglądarce byłaby drugą prawdą, która
 * kiedyś powie co innego niż mail dozoru.
 *
 * Dwa użycia:
 *   - `ZestawienieReakcji` — liczniki (liczone w bazie, `wsparcie_zestawienie`)
 *     i lista zgłoszeń po terminie; dla
 *     super-admina (cała platforma) i dla administratora organizacji
 *     (zestawienie z 21 §2 ust. 7 — Chwila wpływu i chwila Pierwszej reakcji);
 *   - `RejestracjaZgloszenia` — super-admin zapisuje zgłoszenie z poczty,
 *     telefonu albo security@ z Chwilą wpływu ze znacznika wiadomości
 *     (wyzwalacz przyjmuje ją wyłącznie od personelu).
 */

import { Clock, Loader2, PhoneCall } from 'lucide-react';
import { useEffect, useState } from 'react';

import { supabase } from '../../lib/supabase';

export type StanReakcji =
  | 'w_terminie'
  | 'po_terminie'
  | 'czeka'
  | 'czeka_po_terminie'
  | 'zamkniete_bez_reakcji';

export interface WierszReakcji {
  ticket_id: string;
  organization_id: string;
  subject: string;
  kanal: string;
  pakiet_wsparcia: string | null;
  wplynelo_at: string;
  termin_reakcji_at: string | null;
  first_response_at: string | null;
  stan_reakcji: StanReakcji;
  /** 14 dni od Chwili wpływu (Regulamin §24) — liczone w widoku. */
  stan_rozpatrzenia: 'w_terminie' | 'po_terminie' | 'w_toku' | 'w_toku_po_terminie';
}

export const ETYKIETY_STANU: Record<StanReakcji, { tekst: string; klasa: string }> = {
  w_terminie: { tekst: 'Reakcja w terminie', klasa: 'bg-emerald-500/20 text-emerald-400' },
  po_terminie: { tekst: 'Reakcja po terminie', klasa: 'bg-red-500/20 text-red-400' },
  czeka: { tekst: 'Czeka na reakcję', klasa: 'bg-amber-500/20 text-amber-400' },
  czeka_po_terminie: { tekst: 'Bez reakcji — po terminie', klasa: 'bg-red-500/20 text-red-400' },
  zamkniete_bez_reakcji: {
    tekst: 'Zamknięte bez reakcji',
    klasa: 'bg-aurora-bg-subtle text-aurora-muted',
  },
};

export const ETYKIETY_PAKIETU: Record<string, string> = {
  podstawowy: 'Podstawowy · 3 dni robocze',
  rozszerzony: 'Rozszerzony · 1 dzień roboczy',
  priorytet: 'PRIORYTET · 4 h',
};

/** Pakiet bez migawki to usterka zapisu — nie udaje pakietu podstawowego. */
export function etykietaPakietu(pakiet: string | null): string {
  if (!pakiet) return 'pakiet nieustalony';
  return ETYKIETY_PAKIETU[pakiet] ?? pakiet;
}

function dataCzas(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pl-PL', {
    timeZone: 'Europe/Warsaw',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const KOLUMNY =
  'ticket_id, organization_id, subject, kanal, pakiet_wsparcia, wplynelo_at, termin_reakcji_at, first_response_at, stan_reakcji, stan_rozpatrzenia';

/** Ile zgłoszeń po terminie wypisuje lista; liczniki liczy baza po wszystkich. */
export const LIMIT_LISTY_PO_TERMINIE = 200;

export const STANY_PO_TERMINIE: StanReakcji[] = ['czeka_po_terminie', 'po_terminie'];

/** Liczniki z `wsparcie_zestawienie` (migracja 865) — po CAŁYM widoku, nie po stronie. */
export interface Zestawienie {
  reakcja: Partial<Record<StanReakcji, number>>;
  rozpatrzenie: Partial<Record<WierszReakcji['stan_rozpatrzenia'], number>>;
}

export interface ListaPoTerminie {
  wiersze: WierszReakcji[];
  /** Ile zgłoszeń po terminie jest naprawdę — lista może być krótsza. */
  wszystkich: number;
}

/**
 * Liczniki stanów policzone w bazie. Wcześniej liczyła je przeglądarka z pierwszych
 * 500 wierszy widoku, więc przy większej liczbie zgłoszeń zestawienie cicho się
 * obcinało (przegląd PR #1536). `organizationId` zawęża do jednej organizacji.
 */
export async function czytajZestawienie(organizationId?: string): Promise<Zestawienie> {
  const { data, error } = await supabase.rpc('wsparcie_zestawienie', {
    p_organization_id: organizationId ?? null,
  });
  if (error) throw new Error(error.message);
  const wynik: Zestawienie = { reakcja: {}, rozpatrzenie: {} };
  for (const r of (data ?? []) as { os: string; stan: string; liczba: number | string }[]) {
    const liczba = Number(r.liczba);
    if (r.os === 'reakcja') wynik.reakcja[r.stan as StanReakcji] = liczba;
    else if (r.os === 'rozpatrzenie')
      wynik.rozpatrzenie[r.stan as WierszReakcji['stan_rozpatrzenia']] = liczba;
  }
  return wynik;
}

/** Zgłoszenia po terminie (najnowsze), z liczbą wszystkich — lista mówi, gdy jest krótsza. */
export async function czytajPoTerminie(organizationId?: string): Promise<ListaPoTerminie> {
  let q = supabase
    .from('wsparcie_reakcje')
    .select(KOLUMNY, { count: 'exact' })
    .in('stan_reakcji', STANY_PO_TERMINIE)
    .order('wplynelo_at', { ascending: false })
    .limit(LIMIT_LISTY_PO_TERMINIE);
  if (organizationId) q = q.eq('organization_id', organizationId);
  const { data, error, count } = await q;
  if (error) throw new Error(error.message);
  const wiersze = (data ?? []) as unknown as WierszReakcji[];
  return { wiersze, wszystkich: count ?? wiersze.length };
}

/** Stan reakcji dla zgłoszeń widocznych na liście (odznaki), w porcjach po 200. */
export async function czytajStany(ids: string[]): Promise<Map<string, StanReakcji>> {
  const mapa = new Map<string, StanReakcji>();
  for (let i = 0; i < ids.length; i += 200) {
    const { data, error } = await supabase
      .from('wsparcie_reakcje')
      .select('ticket_id, stan_reakcji')
      .in('ticket_id', ids.slice(i, i + 200));
    if (error) throw new Error(error.message);
    for (const r of (data ?? []) as { ticket_id: string; stan_reakcji: StanReakcji }[]) {
      mapa.set(r.ticket_id, r.stan_reakcji);
    }
  }
  return mapa;
}

export function ZestawienieReakcji({
  zestawienie,
  poTerminie,
  ladowanie,
  blad,
}: {
  zestawienie: Zestawienie | null;
  poTerminie: ListaPoTerminie | null;
  ladowanie: boolean;
  blad: string | null;
}) {
  if (ladowanie) {
    return (
      <div className="card p-4 flex items-center gap-2 text-aurora-muted">
        <Loader2 className="w-4 h-4 animate-spin" /> Liczenie czasu reakcji…
      </div>
    );
  }
  if (blad || !zestawienie || !poTerminie) {
    // Błąd odczytu nie jest zerem — mówi o tym wprost.
    return (
      <div className="card p-4 text-red-400 text-sm">
        Nie udało się odczytać czasu reakcji: {blad ?? 'brak danych zestawienia'}
      </div>
    );
  }
  const { wiersze, wszystkich } = poTerminie;

  return (
    <section className="card p-4 space-y-3" aria-label="Czas reakcji wsparcia">
      <h2 className="card__title flex items-center gap-2">
        <Clock className="w-5 h-5 text-amber-400" /> Czas pierwszej reakcji
      </h2>
      <div className="flex flex-wrap gap-2">
        {(Object.keys(ETYKIETY_STANU) as StanReakcji[]).map((s) => (
          <span key={s} className={`badge border-transparent ${ETYKIETY_STANU[s].klasa}`}>
            {ETYKIETY_STANU[s].tekst}:{' '}
            <span className="mono ml-1">{zestawienie.reakcja[s] ?? 0}</span>
          </span>
        ))}
      </div>
      <p className="text-sm text-aurora-muted">
        Rozpatrzenie w 14 dni od wpływu: po terminie{' '}
        <span className="mono">{zestawienie.rozpatrzenie.po_terminie ?? 0}</span>, w toku po
        terminie <span className="mono">{zestawienie.rozpatrzenie.w_toku_po_terminie ?? 0}</span>
      </p>
      {wiersze.length < wszystkich && (
        <p className="text-sm text-amber-400" role="status">
          Lista pokazuje {wiersze.length} najnowszych z {wszystkich} zgłoszeń po terminie — liczniki
          obejmują wszystkie.
        </p>
      )}
      {wiersze.length > 0 && (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-aurora-muted">
              <th className="py-1 pr-2">Zgłoszenie</th>
              <th className="py-1 pr-2">Pakiet</th>
              <th className="py-1 pr-2">Wpłynęło</th>
              <th className="py-1 pr-2">Termin</th>
              <th className="py-1">Reakcja</th>
            </tr>
          </thead>
          <tbody>
            {wiersze.map((w) => (
              <tr key={w.ticket_id} className="border-t border-aurora-border">
                <td className="py-1 pr-2">{w.subject}</td>
                <td className="py-1 pr-2">{etykietaPakietu(w.pakiet_wsparcia)}</td>
                <td className="py-1 pr-2 mono">{dataCzas(w.wplynelo_at)}</td>
                <td className="py-1 pr-2 mono">{dataCzas(w.termin_reakcji_at)}</td>
                <td className="py-1 mono">{dataCzas(w.first_response_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

interface Organizacja {
  id: string;
  name: string;
}

/** Super-admin: zgłoszenie z poczty, telefonu albo security@ z Chwilą wpływu. */
export function RejestracjaZgloszenia({
  uzytkownikId,
  poZapisie,
}: {
  uzytkownikId: string;
  poZapisie: () => void;
}) {
  const [organizacje, setOrganizacje] = useState<Organizacja[]>([]);
  const [otwarte, setOtwarte] = useState(false);
  const [zapis, setZapis] = useState(false);
  const [blad, setBlad] = useState<string | null>(null);
  const [form, setForm] = useState({
    organizationId: '',
    kanal: 'email',
    wplynelo: '',
    temat: '',
    opis: '',
  });

  useEffect(() => {
    if (!otwarte || organizacje.length > 0) return;
    void supabase
      .from('organizations')
      .select('id, name')
      .order('name')
      .then(({ data }) => setOrganizacje((data ?? []) as Organizacja[]));
  }, [otwarte, organizacje.length]);

  const zapisz = async () => {
    setBlad(null);
    if (!form.organizationId || !form.wplynelo || !form.temat.trim()) {
      setBlad('Organizacja, chwila wpływu i temat są wymagane.');
      return;
    }
    setZapis(true);
    const { error } = await supabase.from('support_tickets').insert({
      organization_id: form.organizationId,
      created_by: uzytkownikId,
      subject: form.temat.trim(),
      description: form.opis.trim() || form.temat.trim(),
      kanal: form.kanal,
      // `datetime-local` to czas lokalny przeglądarki; Date oddaje go w UTC.
      wplynelo_at: new Date(form.wplynelo).toISOString(),
    });
    setZapis(false);
    if (error) {
      setBlad(error.message);
      return;
    }
    setForm({ organizationId: '', kanal: 'email', wplynelo: '', temat: '', opis: '' });
    setOtwarte(false);
    poZapisie();
  };

  if (!otwarte) {
    return (
      <button className="btn btn--secondary" onClick={() => setOtwarte(true)}>
        <PhoneCall className="w-4 h-4" /> Zarejestruj zgłoszenie z poczty lub telefonu
      </button>
    );
  }

  return (
    <section className="card p-4 space-y-3" aria-label="Rejestracja zgłoszenia">
      <h2 className="card__title">Zgłoszenie z poczty, telefonu albo security@</h2>
      <p className="text-sm text-aurora-muted">
        Chwila wpływu to znacznik czasu wiadomości albo rozmowy — od niej liczy się termin reakcji z
        Pakietu wsparcia organizacji.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <select
          className="input"
          aria-label="Organizacja"
          value={form.organizationId}
          onChange={(e) => setForm({ ...form, organizationId: e.target.value })}
        >
          <option value="">Organizacja…</option>
          {organizacje.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
        <select
          className="input"
          aria-label="Kanał"
          value={form.kanal}
          onChange={(e) => setForm({ ...form, kanal: e.target.value })}
        >
          <option value="email">Poczta elektroniczna</option>
          <option value="telefon">Telefon</option>
          <option value="security">Zgłoszenie bezpieczeństwa (security@)</option>
        </select>
        <input
          className="input"
          type="datetime-local"
          aria-label="Chwila wpływu"
          value={form.wplynelo}
          onChange={(e) => setForm({ ...form, wplynelo: e.target.value })}
        />
        <input
          className="input"
          placeholder="Temat"
          value={form.temat}
          onChange={(e) => setForm({ ...form, temat: e.target.value })}
        />
      </div>
      <textarea
        className="input w-full"
        placeholder="Opis"
        rows={3}
        value={form.opis}
        onChange={(e) => setForm({ ...form, opis: e.target.value })}
      />
      {blad && <p className="text-sm text-red-400">{blad}</p>}
      <div className="flex gap-2">
        <button className="btn btn--primary" disabled={zapis} onClick={() => void zapisz()}>
          {zapis ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Zapisz zgłoszenie
        </button>
        <button className="btn btn--ghost" onClick={() => setOtwarte(false)}>
          Anuluj
        </button>
      </div>
    </section>
  );
}
